import { Input } from "./ui/input";
import { isVeniceVariant } from "./VeniceSubPicker";

// Locations that never need a "Specific address" input because the pickup /
// drop-off point is already unambiguous (a specific venue / retail complex,
// or a Venice sub-variant that has its own address picker).
const NO_SPECIFIC_ADDRESS = new Set([
  "Noventa Outlet",
  "Designer Outlets Noventa",
  "Noventa di Piave Designer Outlet",
]);

/**
 * Renders a "Specific address" checkbox under a location field. When the
 * checkbox is ticked, a text input appears for the customer to enter an
 * address within 15 km of the selected location (accessible by car).
 *
 * Hidden entirely when the location is empty, is a Venice variant (Venice has
 * its dedicated "Near Venice" sub-picker) or is a known venue-only destination
 * such as the Designer Outlets Noventa.
 */
export default function SpecificAddressField({
  location,
  value,
  onChange,
  testIdPrefix = "loc",
  sideLabel = "main location",
}) {
  if (!location) return null;
  if (isVeniceVariant(location)) return null;
  if (NO_SPECIFIC_ADDRESS.has((location || "").trim())) return null;

  const enabled = typeof value === "string" && value.length > 0;
  // The checkbox is "on" when the user has typed any address OR clicked it.
  // We use the address presence as the source of truth and an explicit toggle
  // clears the value.
  const toggle = (checked) => {
    if (checked) {
      // Reveal the input by setting an empty string sentinel (vs undefined/null
      // which hides). The required validation in the parent triggers if blank
      // at submit time.
      onChange(value || " ");
    } else {
      onChange("");
    }
  };

  return (
    <div className="mt-2 space-y-2" data-testid={`${testIdPrefix}-specific-address-wrap`}>
      <label
        className="flex items-center gap-2 text-white/70 text-xs cursor-pointer"
        data-testid={`${testIdPrefix}-specific-address-toggle-label`}
      >
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
          className="accent-[#C5A059]"
          data-testid={`${testIdPrefix}-specific-address-toggle`}
        />
        Specific address
      </label>
      {enabled && (
        <div>
          <Input
            value={value.trim() === "" ? "" : value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Address / hotel within 15 km of ${location} (accessible by car)`}
            className="input-dark"
            maxLength={300}
            data-testid={`${testIdPrefix}-specific-address-input`}
          />
          <p className="text-white/40 text-[10px] mt-1">
            Address / hotel within 15 km of {sideLabel} (accessible by car).
          </p>
        </div>
      )}
    </div>
  );
}
